/**
 * Organization service for API calls
 */
import { httpGet, httpPost, httpPatch, httpDelete } from '@src/v3/utils/http';
import {
  Organization,
  OrganizationMembership,
  OrganizationMembershipListItem,
  Team,
  TeamMember,
  OrganizationTransaction,
  BillingSummary,
  MyOrganizationResponse,
  PendingInvitesResponse,
  MembershipRole,
  DashboardDataResponse,
  MemberPerformance,
  ApplicationBreakdownResponse,
  ApplicationBreakdownParams,
  ApplicationGeneralLink,
  ApplicationSource,
  CoapplicantGuarantor,
} from '@src/v3/interfaces/organization';
import { OrganizationGeneralLinksListApi } from '@src/v3/interfaces/api/generalApplication';

const BASE_URL = '/organizations';

// ============ Organization API ============

/**
 * Get the current user's organization membership
 */
export const getMyOrganization = async (): Promise<MyOrganizationResponse> => {
  const response = await httpGet(`${BASE_URL}/me/`);
  return response; // httpGet already returns .data
};

/**
 * Get organization details
 */
export const getOrganization = async (orgId: string): Promise<Organization> => {
  const response = await httpGet(`${BASE_URL}/${orgId}/`);
  return response;
};

/**
 * Update organization details (supports file uploads for logo)
 */
export const updateOrganization = async (
  orgId: string,
  data: { logo?: File | null; name?: string }
): Promise<Organization> => {
  // Use FormData if uploading a file
  if (data.logo instanceof File) {
    const formData = new FormData();
    formData.append('logo', data.logo);
    if (data.name) formData.append('name', data.name);

    const response = await httpPatch(`${BASE_URL}/${orgId}/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response;
  }

  // For removing logo (null) or other updates, use JSON
  const response = await httpPatch(`${BASE_URL}/${orgId}/`, data);
  return response;
};

// ============ Membership API ============

/**
 * Get all members of an organization
 */
export const getOrganizationMembers = async (
  orgId: string,
  includeOffboarded = false
): Promise<OrganizationMembershipListItem[]> => {
  const response = await httpGet(
    `${BASE_URL}/${orgId}/members/?include_offboarded=${includeOffboarded}`
  );
  return response;
};

/**
 * Invite a new member to the organization.
 *
 * Seat Requirements:
 * - SUPERADMIN/ADMIN roles always require a seat
 * - Team Managers (assigned to manage at least one team) require a seat
 * - Regular MEMBERs do NOT require seats
 *
 * @param orgId - Organization ID
 * @param email - Email to invite
 * @param role - Role to assign
 * @param confirmReactivateSeat - If true, will reactivate a disabled seat if needed
 * @param teamAssignments - List of team IDs to assign (as manager for TEAM_MANAGER, as member for MEMBER)
 * @param newTeams - List of new team names to create and assign
 * @param isManager - Whether to assign as manager (true for TEAM_MANAGER role)
 */
export const inviteMember = async (
  orgId: string,
  email: string,
  role: MembershipRole,
  confirmReactivateSeat: boolean = false,
  teamAssignments: string[] = [],
  newTeams: string[] = [],
  isManager: boolean = false
): Promise<OrganizationMembership> => {
  const response = await httpPost(`${BASE_URL}/${orgId}/members/`, {
    email,
    role,
    confirm_reactivate_seat: confirmReactivateSeat,
    team_manager_assignments: isManager ? teamAssignments : [],
    team_member_assignments: !isManager ? teamAssignments : [],
    new_teams: newTeams,
    new_teams_as_manager: isManager,
  });
  return response;
};

/**
 * Cancel a pending invitation
 */
export const cancelInvitation = async (
  membershipId: string
): Promise<{ message: string }> => {
  const response = await httpPost(
    `${BASE_URL}/members/${membershipId}/cancel-invitation/`,
    {}
  );
  return response;
};

/**
 * Resend a pending invitation
 */
export const resendInvitation = async (
  membershipId: string
): Promise<{ message: string }> => {
  const response = await httpPost(
    `${BASE_URL}/members/${membershipId}/resend-invitation/`,
    {}
  );
  return response;
};

/**
 * Update a member's role
 */
export const updateMemberRole = async (
  membershipId: string,
  role: MembershipRole
): Promise<OrganizationMembership> => {
  const response = await httpPatch(`${BASE_URL}/members/${membershipId}/`, {
    role,
  });
  return response;
};

/**
 * Permanently delete a historical member record (rejected/offboarded).
 */
export const deleteMemberRecord = async (
  membershipId: string
): Promise<{ message: string }> => {
  const response = await httpDelete(`${BASE_URL}/members/${membershipId}/`, {});
  return response;
};

/**
 * Offboard a member
 */
export const offboardMember = async (
  membershipId: string
): Promise<{ message: string }> => {
  const response = await httpPost(
    `${BASE_URL}/members/${membershipId}/offboard/`,
    {}
  );
  return response;
};

// ============ Invitation API ============

/**
 * Get pending organization invites for current user
 */
export const getPendingInvites = async (): Promise<PendingInvitesResponse> => {
  const response = await httpGet(`${BASE_URL}/invites/pending/`);
  return response;
};

/**
 * Validate an invite token (public endpoint)
 */
export const validateInviteToken = async (
  token: string
): Promise<{
  is_valid: boolean;
  organization_name: string;
  role: MembershipRole;
}> => {
  const response = await httpGet(
    `${BASE_URL}/invites/validate/?token=${token}`
  );
  return response;
};

/**
 * Accept an organization invite
 */
export const acceptInvite = async (
  token: string
): Promise<OrganizationMembership> => {
  const response = await httpPost(`${BASE_URL}/invites/${token}/accept/`, {});
  return response;
};

/**
 * Reject an organization invite
 */
export const rejectInvite = async (
  token: string
): Promise<{ message: string }> => {
  const response = await httpPost(`${BASE_URL}/invites/${token}/reject/`, {});
  return response;
};

// ============ Team API ============

/**
 * Get all teams in an organization
 */
export const getTeams = async (orgId: string): Promise<Team[]> => {
  const response = await httpGet(`${BASE_URL}/${orgId}/teams/`);
  return response;
};

/**
 * Create a new team
 */
export const createTeam = async (
  orgId: string,
  name: string,
  managerMembershipId?: string | null
): Promise<Team> => {
  const response = await httpPost(`${BASE_URL}/${orgId}/teams/`, {
    name,
    manager_membership_id: managerMembershipId || null,
  });
  return response;
};

/**
 * Get team details
 */
export const getTeam = async (teamId: string): Promise<Team> => {
  const response = await httpGet(`${BASE_URL}/teams/${teamId}/`);
  return response;
};

/**
 * Update team details
 */
export const updateTeam = async (
  teamId: string,
  data: { name?: string; description?: string }
): Promise<Team> => {
  const response = await httpPatch(`${BASE_URL}/teams/${teamId}/`, data);
  return response;
};

/**
 * Delete a team
 */
export const deleteTeam = async (
  teamId: string
): Promise<{ message: string }> => {
  const response = await httpDelete(`${BASE_URL}/teams/${teamId}/`);
  return response;
};

/**
 * Get team members
 */
export const getTeamMembers = async (teamId: string): Promise<TeamMember[]> => {
  const response = await httpGet(`${BASE_URL}/teams/${teamId}/members/`);
  return response;
};

/**
 * Add member to team
 */
export const addTeamMember = async (
  teamId: string,
  membershipId: string,
  isManager: boolean = false
): Promise<{ message: string }> => {
  const response = await httpPost(`${BASE_URL}/teams/${teamId}/members/`, {
    membership_id: membershipId,
    is_manager: isManager,
  });
  return response;
};

/**
 * Remove member from team
 */
export const removeTeamMember = async (
  teamId: string,
  membershipId: string
): Promise<{ message: string }> => {
  const response = await httpDelete(
    `${BASE_URL}/teams/${teamId}/members/${membershipId}/`
  );
  return response;
};

/**
 * Set or unset a member as team manager
 */
export const setTeamManager = async (
  teamId: string,
  membershipId: string,
  isManager: boolean
): Promise<{ message: string }> => {
  const response = await httpPatch(
    `${BASE_URL}/teams/${teamId}/members/${membershipId}/manager/`,
    { is_manager: isManager }
  );
  return response;
};

// ============ Billing API ============

/**
 * Get billing summary
 */
export const getBillingSummary = async (
  orgId: string
): Promise<BillingSummary> => {
  const response = await httpGet(`${BASE_URL}/${orgId}/billing/`);
  return response;
};

/**
 * Purchase additional seats
 * @param orgId - Organization ID
 * @param seatCount - Number of seats to purchase
 * @param paymentMethod - Optional payment method to add if org doesn't have one
 */
export const purchaseSeats = async (
  orgId: string,
  seatCount: number,
  paymentMethod?: { token: string; type: 'APPLE_PAY' | 'GOOGLE_PAY' | 'TOKEN' }
): Promise<OrganizationTransaction> => {
  const payload: Record<string, unknown> = { seat_count: seatCount };
  if (paymentMethod) {
    payload.token = paymentMethod.token;
    payload.type = paymentMethod.type;
  }
  const response = await httpPost(
    `${BASE_URL}/${orgId}/billing/seats/`,
    payload
  );
  return response;
};

/**
 * Disable a seat (won't renew next billing cycle, will be wiped)
 */
export const disableSeat = async (
  orgId: string,
  seatId: string
): Promise<{ message: string }> => {
  const response = await httpDelete(
    `${BASE_URL}/${orgId}/billing/seats/${seatId}/`
  );
  return response;
};

// Alias for backwards compatibility
export const cancelSeat = disableSeat;

/**
 * Reactivate a disabled seat (will renew next billing cycle)
 */
export const reactivateSeat = async (
  orgId: string,
  seatId: string
): Promise<{ message: string }> => {
  const response = await httpPost(
    `${BASE_URL}/${orgId}/billing/seats/${seatId}/reactivate/`,
    {}
  );
  return response;
};

/**
 * Update payment method
 */
export const updatePaymentMethod = async (
  orgId: string,
  token: string,
  type: 'APPLE_PAY' | 'GOOGLE_PAY' | 'TOKEN'
): Promise<{ message: string }> => {
  const response = await httpPatch(
    `${BASE_URL}/${orgId}/billing/payment-method/`,
    { token, type }
  );
  return response;
};

/**
 * Initial purchase - sets up billing with payment method and purchases minimum seats
 */
export const initialPurchase = async (
  orgId: string,
  token: string,
  type: 'APPLE_PAY' | 'GOOGLE_PAY' | 'TOKEN'
): Promise<OrganizationTransaction> => {
  const response = await httpPost(
    `${BASE_URL}/${orgId}/billing/initial-purchase/`,
    { token, type }
  );
  return response;
};

/**
 * Get organization transactions
 */
export const getTransactions = async (
  orgId: string,
  limit = 20,
  offset = 0
): Promise<{ results: OrganizationTransaction[]; count: number }> => {
  const response = await httpGet(
    `${BASE_URL}/${orgId}/transactions/?limit=${limit}&offset=${offset}`
  );
  return response;
};

// ============ Post on Behalf API ============

export interface PropertySlotsInfo {
  agent_id: string;
  agent_name: string;
  has_available_slot: boolean;
  free_properties_allowed: number;
  current_open_properties: number;
  available_slots: number;
  plan_type: string;
  has_payment_method: boolean;
  has_active_subscription: boolean;
}

export interface MemberForPosting {
  membership_id: string;
  agent_id: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  role: string;
  // Additional fields for property preview
  profile_picture?: string | null;
  profile_picture_thumbnail?: string | null;
  company_name?: string;
  bio?: string;
  profile_url_identifier?: string;
}

/**
 * Check if an agent has available property slots
 */
export const checkAgentPropertySlots = async (
  agentId: string
): Promise<PropertySlotsInfo> => {
  const response = await httpGet(
    `${BASE_URL}/agents/${agentId}/property-slots/`
  );
  return response;
};

/**
 * Get list of members the user can post properties for
 */
export const getMembersForPosting = async (): Promise<{
  members: MemberForPosting[];
}> => {
  const response = await httpGet(`${BASE_URL}/members-for-posting/`);
  return response;
};

/**
 * Agent documents response for posting on behalf
 */
export interface AgentDocumentsResponse {
  custom_documents: Array<{
    id: string;
    name: string;
    category: string;
    is_editable: boolean;
    is_deletable: boolean;
    is_icon_editable: boolean;
    icon: string | null;
    description: string | null;
    global_instruction: string | null;
    template_id: string | null;
  }>;
  template_documents: Array<{
    id: string;
    name: string;
    category: string;
    is_editable: boolean;
    is_deletable: boolean;
    is_icon_editable: boolean;
    icon: string | null;
    description: string | null;
    global_instruction: string | null;
    template_id: string | null;
  }>;
  zeme_required_documents: Array<{
    id: string;
    name: string;
    category: string;
    is_editable: boolean;
    is_deletable: boolean;
    is_icon_editable: boolean;
    icon: string | null;
    description: string | null;
    global_instruction: string | null;
    template_id: string | null;
  }>;
  agent_id: string;
  agent_name: string;
}

/**
 * Get documents for a specific agent when posting on their behalf
 */
export const getAgentDocumentsForPosting = async (
  agentId: string
): Promise<AgentDocumentsResponse> => {
  const response = await httpGet(`${BASE_URL}/agents/${agentId}/documents/`);
  return response;
};

// ============ Agent Properties & Applications API ============

export interface AgentPropertiesResponse {
  count: number;
  results: unknown[]; // Property data from backend
  agent_id: string;
  agent_name: string;
}

export interface AgentApplicationsResponse {
  count: number;
  results: unknown[]; // Application data from backend
  agent_id: string;
  agent_name: string;
}

/**
 * Get properties for a specific agent (admin panel)
 */
export const getAgentProperties = async (
  agentId: string,
  params?: {
    is_open?: boolean;
    include_collaborator_listings?: boolean;
    limit?: number;
    offset?: number;
  }
): Promise<AgentPropertiesResponse> => {
  const queryParams = new URLSearchParams();
  if (params?.is_open !== undefined) {
    queryParams.append('is_open', String(params.is_open));
  }
  if (params?.include_collaborator_listings !== undefined) {
    queryParams.append(
      'include_collaborator_listings',
      String(params.include_collaborator_listings)
    );
  }
  if (params?.limit !== undefined) {
    queryParams.append('limit', String(params.limit));
  }
  if (params?.offset !== undefined) {
    queryParams.append('offset', String(params.offset));
  }
  const queryString = queryParams.toString();
  const url = `${BASE_URL}/agents/${agentId}/properties/${
    queryString ? `?${queryString}` : ''
  }`;
  const response = await httpGet(url);
  return response;
};

/**
 * Get applications for a specific agent's properties (admin panel)
 */
export const getAgentApplications = async (
  agentId: string,
  params?: { status?: string; limit?: number; offset?: number }
): Promise<AgentApplicationsResponse> => {
  const queryParams = new URLSearchParams();
  if (params?.status) {
    queryParams.append('status', params.status);
  }
  if (params?.limit !== undefined) {
    queryParams.append('limit', String(params.limit));
  }
  if (params?.offset !== undefined) {
    queryParams.append('offset', String(params.offset));
  }
  const queryString = queryParams.toString();
  const url = `${BASE_URL}/agents/${agentId}/applications/${
    queryString ? `?${queryString}` : ''
  }`;
  const response = await httpGet(url);
  return response;
};

// ============ General Application Links API ============

export interface OrganizationGeneralLinksParams {
  /** Narrows to one agent's links; an agent outside the visible set is an empty page. */
  agentId?: string;
  limit?: number;
  offset?: number;
}

/**
 * Get the General Application links owned by the agents the caller can see in
 * this organization, newest first, each with its own application counts.
 *
 * Rows come back in the API's shape (the agent-facing link serializer plus the
 * owner), so every surface that renders a link row shares one type.
 *
 * @param {string} orgId - Organization ID
 * @param {OrganizationGeneralLinksParams} [params] - Agent filter and pagination
 * @return {Promise<OrganizationGeneralLinksListApi>}
 */
export const getOrganizationGeneralLinks = async (
  orgId: string,
  params?: OrganizationGeneralLinksParams
): Promise<OrganizationGeneralLinksListApi> => {
  const queryParams = new URLSearchParams();
  if (params?.agentId) {
    queryParams.append('agent_id', params.agentId);
  }
  if (params?.limit !== undefined) {
    queryParams.append('limit', String(params.limit));
  }
  if (params?.offset !== undefined) {
    queryParams.append('offset', String(params.offset));
  }
  const queryString = queryParams.toString();
  const url = `${BASE_URL}/${orgId}/general-links/${
    queryString ? `?${queryString}` : ''
  }`;
  const response = await httpGet(url);
  return response;
};

// ============ Dashboard Analytics API ============

/**
 * Dashboard query parameters
 */
export interface DashboardQueryParams {
  startDate?: string;
  endDate?: string;
  groupBy?: 'day' | 'week' | 'month';
  team?: string;
}

/**
 * One member-performance row, snake_case → camelCase. `member_performance` and
 * `top_performers` are the same rows (the API's top three by application count),
 * so they share one mapper instead of drifting apart.
 *
 * @param {Record<string, unknown>} member - A raw `member_performance` row
 * @return {MemberPerformance}
 */
const mapMemberPerformance = (
  member: Record<string, unknown>
): MemberPerformance => ({
  membershipId: member.membership_id as string,
  agentId: member.agent_id as string,
  name: member.name as string,
  email: member.email as string,
  profilePicture: member.profile_picture as string | null,
  role: member.role as MembershipRole,
  teams: member.teams as string[],
  submitted: member.submitted as number,
  inProgress: member.in_progress as number,
  accepted: member.accepted as number,
  totalApplications: member.total_applications as number,
  generalApplications: (member.general_applications as number) ?? 0,
  properties: member.properties as number,
  openProperties: member.open_properties as number,
  generalLinks: (member.general_links as number) ?? 0,
});

/**
 * Get organization dashboard analytics data
 */
export const getDashboardData = async (
  orgId: string,
  params?: DashboardQueryParams
): Promise<DashboardDataResponse> => {
  const queryParams = new URLSearchParams();
  if (params?.startDate) {
    queryParams.append('start_date', params.startDate);
  }
  if (params?.endDate) {
    queryParams.append('end_date', params.endDate);
  }
  if (params?.groupBy) {
    queryParams.append('group_by', params.groupBy);
  }
  if (params?.team) {
    queryParams.append('team', params.team);
  }
  const queryString = queryParams.toString();
  const url = `${BASE_URL}/${orgId}/dashboard/${
    queryString ? `?${queryString}` : ''
  }`;
  const response = await httpGet(url);

  // Transform snake_case response to camelCase
  return {
    stats: {
      members: response.stats.members,
      properties: response.stats.properties,
      // Defaulted, not optional: the card always has a number to show, even
      // against an API that predates the counter.
      generalLinks: {
        total: response.stats.general_links?.total ?? 0,
        active: response.stats.general_links?.active ?? 0,
      },
      applications: {
        total: response.stats.applications.total,
        submitted: response.stats.applications.submitted,
        inProgress: response.stats.applications.in_progress,
        accepted: response.stats.applications.accepted,
      },
    },
    timeSeries: (response.time_series || []).map(
      (point: Record<string, unknown>) => ({
        date: point.date as string,
        total: point.total as number,
        requested: (point.requested as number) || 0,
        submitted: point.submitted as number,
        inProgress: point.in_progress as number,
        accepted: point.accepted as number,
      })
    ),
    memberPerformance: (response.member_performance || []).map(
      mapMemberPerformance
    ),
    recentApplications: (response.recent_applications || []).map(
      (app: Record<string, unknown>) => ({
        id: app.id as string,
        propertyId: app.property_id as string | null,
        propertyAddress: app.property_address as string | null,
        status: app.status as string | null,
        createdAt: app.created_at as string | null,
        agentId: app.agent_id as string | null,
      })
    ),
    topPerformers: (response.top_performers || []).map(mapMemberPerformance),
  };
};

/**
 * Get paginated application breakdown for the admin dashboard
 */
export const getApplicationBreakdown = async (
  orgId: string,
  params?: ApplicationBreakdownParams
): Promise<ApplicationBreakdownResponse> => {
  const queryParams = new URLSearchParams();
  if (params?.startDate) queryParams.append('start_date', params.startDate);
  if (params?.endDate) queryParams.append('end_date', params.endDate);
  if (params?.search) queryParams.append('search', params.search);
  if (params?.team) queryParams.append('team', params.team);
  if (params?.status) queryParams.append('status', params.status);
  if (params?.date) queryParams.append('date', params.date);
  if (params?.limit !== undefined)
    queryParams.append('limit', String(params.limit));
  if (params?.offset !== undefined)
    queryParams.append('offset', String(params.offset));
  if (params?.ordering) queryParams.append('ordering', params.ordering);

  const queryString = queryParams.toString();
  const url = `${BASE_URL}/${orgId}/dashboard/applications/${
    queryString ? `?${queryString}` : ''
  }`;
  const response = await httpGet(url);

  return {
    count: response.count as number,
    results: (response.results || []).map((item: Record<string, unknown>) => ({
      id: item.id as string,
      creationDate: item.creation_date as string,
      agentId: item.agent_id as string,
      agentName: item.agent_name as string,
      agentProfilePicture: item.agent_profile_picture as string | null,
      agentTeams: item.agent_teams as string[],
      // Both new keys default to the property reading, so a row served by an API
      // that predates them still renders exactly as it did before.
      isGeneral: item.is_general === true,
      source: (item.source as ApplicationSource) ?? 'property',
      // `{id, name}` needs no key translation, so the row's own object is the
      // mapped value.
      generalLink: (item.general_link as ApplicationGeneralLink | null) ?? null,
      propertyId: (item.property_id as string | null) ?? null,
      propertyAddress: item.property_address as string,
      rentAmount: item.rent_amount as number | null,
      primaryApplicant: item.primary_applicant as string,
      coapplicantsGuarantors: (
        (item.coapplicants_guarantors as Array<Record<string, string>>) || []
      ).map((cg) => ({ name: cg.name, role: cg.role }) as CoapplicantGuarantor),
      status: item.status as string,
    })),
  };
};
